import React from 'react';

/**
 * Divider Component - Atomic Level
 * 
 * A flexible divider component with optional text content.
 * 
 * Features:
 * - Horizontal and vertical orientations
 * - Optional text/content in the middle
 * - Customizable spacing
 * - Different color variants
 */

export interface DividerProps {
  /** Orientation of the divider */
  orientation?: 'horizontal' | 'vertical';
  /** Content to display in the middle of the divider */
  children?: React.ReactNode;
  /** Additional class names */
  className?: string;
  /** Color variant */
  variant?: 'default' | 'light' | 'dark';
}

const variantClasses = {
  default: 'border-gray-300',
  light: 'border-gray-200',
  dark: 'border-gray-400',
};

export const Divider: React.FC<DividerProps> = ({
  orientation = 'horizontal',
  children,
  className = '',
  variant = 'default',
}) => {
  if (orientation === 'vertical') {
    return (
      <div
        className={`border-l ${variantClasses[variant]} h-full ${className}`}
        role="separator"
        aria-orientation="vertical"
      />
    );
  }

  if (children) {
    return (
      <div
        className={`flex items-center ${className}`}
        role="separator"
        aria-orientation="horizontal"
      >
        <div className={`flex-1 border-t ${variantClasses[variant]}`} />
        <span className="px-4 text-sm text-gray-500 font-medium">{children}</span>
        <div className={`flex-1 border-t ${variantClasses[variant]}`} />
      </div>
    );
  }

  return (
    <hr
      className={`border-t ${variantClasses[variant]} ${className}`}
      role="separator"
      aria-orientation="horizontal"
    />
  );
};

export default Divider;

